import {
  CaretLeft,
  Paperclip,
  XCircle,
  X,
  PlayCircle,
} from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import useOrderItemDetails from "../../hooks/useOrderItemDetails";
import { HashLoader } from "react-spinners";
import { formatAsMoney } from "../../helpers/numbers";
import { useState } from "react";

const RETURN_REASONS = [
  "Size or fit issue",
  "Wrong item received",
  "Product not as described",
  "Faulty or damaged on arrival",
  "Poor quality or workmanship",
  "Color variation from display",
  "Product looks used or refurbished",
  "Missing parts or accessories",
  "Expired or defective product",
  "Delayed delivery",
  "Packaging was tampered or open",
  "Found a better price elsewhere",
  "Changed my mind / no longer needed",
  "Received duplicate order",
  "Other...",
];

const ReturnItem = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { orderItem, isLoading } = useOrderItemDetails(itemId!);

  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [details, setDetails] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const handleCheckboxChange = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles([...files, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <HashLoader />
      </div>
    );
  }

  const item = orderItem?.data;
  if (!item) {
    return (
      <div className="p-5 aspect-video flex items-center justify-center">
        Order item not found
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-5 px-5 pb-20">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 bg-white w-full">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
        >
          <CaretLeft weight="bold" />
          <span>Back</span>
        </button>
        <h1 className="text-lg font-medium ml-4">Return Item</h1>
      </div>

      <div className="flex flex-col gap-5 w-full md:px-5">
        {/* Intro */}
        <div>
          <div className="font-semibold text-xl">
            Tell us why you want to return this item
          </div>
          <div className="text-sm text-gray-600 mt-1">
            We’re sorry things didn’t work out. Please share the reason(s) for
            your return so we can make things right and improve your experience.
          </div>
        </div>

        {/* Product info */}
        <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
          <img
            src={item.product?.images?.[0]?.path}
            alt={item.product?.name || "Product Image"}
            className="w-16 h-16 rounded-md object-cover bg-gray-200"
          />
          <div className="flex-1">
            <div className="font-medium truncate">
              {item.product?.name || "Could not fetch product name"}
            </div>
            <div className="text-sm opacity-70">
              Qty: {item?.quantity || "Could not fetch quantity"}
            </div>
          </div>
          <div className="font-medium text-lg">
            ₦ {formatAsMoney(parseFloat(item?.subtotal || "0"))}
          </div>
        </div>

        {/* Checklist */}
        <div className="bg-white p-4 rounded-lg border flex flex-col gap-3">
          <div className="font-medium text-gray-800">
            Select all that apply:
          </div>
          {RETURN_REASONS.map((reason) => (
            <label
              key={reason}
              className="flex items-center gap-2 cursor-pointer text-gray-700"
            >
              <input
                type="checkbox"
                checked={selectedReasons.includes(reason)}
                onChange={() => handleCheckboxChange(reason)}
                className="w-4 h-4 accent-deepBlue"
              />
              <span>{reason}</span>
            </label>
          ))}
        </div>

        {/* Additional details */}
        <div>
          <label className="font-medium text-sm mb-2 block">
            Provide more details (optional)
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3"
            rows={5}
            placeholder="Tell us more about what went wrong — e.g., what was faulty, what you expected, etc."
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          ></textarea>
          <div className="text-sm text-gray-500 mt-1">
            The more context you provide, the faster we can process your return.
          </div>
        </div>

        {/* Attachments */}
        <div className="flex flex-col gap-3">
          {files.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              {files.map((file, i) => (
                <div
                  key={i}
                  className="h-24 aspect-square rounded-lg overflow-hidden bg-gray-100 group relative cursor-pointer"
                  onClick={() => setPreviewIndex(i)}
                >
                  {/* Preview */}
                  {file.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt="attachment"
                      className="h-24 w-24 object-cover"
                    />
                  ) : (
                    <div className="relative">
                      <video
                        src={URL.createObjectURL(file)}
                        className="h-24 aspect-video object-cover"
                        muted
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <PlayCircle
                          weight="fill"
                          className="text-white"
                          size={24}
                        />
                      </div>
                    </div>
                  )}

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFile(i);
                    }}
                    className="absolute top-1 right-1 bg-black/60 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                    title="Remove file"
                  >
                    <XCircle size={18} weight="fill" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="font-medium text-sm mb-2 block">
            Attach images or videos (optional)
          </label>
          <div className="flex items-center gap-3">
            <label
              htmlFor="attachments"
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition cursor-pointer px-4 py-2 rounded-lg border"
            >
              <Paperclip weight="bold" />
              <span>Attach Files</span>
            </label>
            <input
              id="attachments"
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Submit button */}
        <button className="p-2 self-end py-3 text-center bg-deepBlue text-white px-10 ring-2 ring-deepBlue/50 rounded-xl font-medium hover:bg-deepBlue/90 transition">
          Submit Return Request
        </button>

        <div className="text-sm text-gray-500 text-center mt-2">
          Once submitted, our support team will review your request and reach
          out with next steps within 24–48 hours.
        </div>
      </div>

      {/* Fullscreen Preview Modal */}
      {previewIndex !== null && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setPreviewIndex(null)}
        >
          <button
            className="absolute top-10 right-10 bg-black/60 text-white p-2 rounded-full"
            onClick={() => setPreviewIndex(null)}
          >
            <X size={24} weight="bold" />
          </button>
          <div
            className="relative max-w-3xl w-full max-h-[90vh] flex justify-center items-center"
            onClick={(e) => e.stopPropagation()}
          >
            {files[previewIndex].type.startsWith("image/") ? (
              <img
                src={URL.createObjectURL(files[previewIndex])}
                alt="Preview"
                className="max-h-[90vh] object-contain rounded-lg"
              />
            ) : (
              <video
                src={URL.createObjectURL(files[previewIndex])}
                controls
                autoPlay
                className="max-h-[90vh] object-contain rounded-lg"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnItem;
